/*
 * ====================================================================================
 * FILE: EquipmentPage.jsx  (renamed from DeviceManagementPage.jsx)
 * PURPOSE: The EQUIPMENT inventory — every machine in the fleet. "Device" in
 *          this product means the TCU telematics hardware (see HardwarePage),
 *          so this page is named after what it actually lists: equipment.
 *
 * DESCRIPTION:
 * This page lists every machine owned or managed by the company. It combines:
 * 1. Static Asset Data: Model, Brand, Serial Number, Category.
 * 2. Live Telemetry Data: Current Status, Engine hours, Last-seen, Location.
 *
 * FEATURES:
 * - KPI / filter strip: click a card to filter by status (or TCU-fitted).
 * - Search: instant text search by ID, tracker, model or brand.
 * - Status + Site dropdown filters, with a one-tap "Clear".
 * - Bulk select + CSV export of the selected (or all filtered) rows.
 * - Navigation: clicking a row opens that device's detail page.
 *
 * Design: implements the "Device Management" Claude-Design layout (compact KPI
 * cards, split Asset/Tracker column, engine-hours, last-seen signal dot, polished
 * status pills, search-off empty state) on the app's theme tokens.
 * ====================================================================================
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom'; // For page navigation

// --- UI Components (Material UI) ---
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  TextField,
  InputAdornment,
  Checkbox,
  Pagination,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  Autocomplete,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';

// --- Icons ---
import {
  Search as SearchIcon,
  SearchOff as SearchOffIcon,
  Add as AddIcon,
  Backup as BackupIcon,
  FileDownloadOutlined as ExportIcon,
  LocationOn as MapPinIcon,
  ChevronRight as ChevronRightIcon,
  Inventory2 as Inventory2Icon,
  SettingsRemote as SettingsRemoteIcon,
  DoNotDisturbOn as DoNotDisturbOnIcon,
  HourglassTop as HourglassTopIcon,
  TaskAlt as TaskAltIcon,
  Build as BuildIcon,
  WarningAmber as WarningAmberIcon,
  Engineering as EngineeringIcon,
  WbIncandescent as WbIncandescentIcon,
  BatteryChargingFull as BatteryChargingFullIcon,
} from '@mui/icons-material';

// --- API Client ---
import client from '../../api/client';
import deviceManagementService from '../../services/deviceManagementService';

// --- Utilities ---
import { parseDeviceId } from '../../utils/deviceUtils';
import { useDeviceStore } from '../../store/DeviceStore';
import { statusLabel } from '../../utils/statusLabels';
import { resolveDeviceStatus, STATUS_META } from '../../utils/liveStatus';
import { canChangeMachineStatus, isSuperAdmin } from '../../utils/permissions';
import { machineLabel, isSerialMissing } from '../../utils/machineLabel';
import { useAuth } from '../../context/AuthContext';
import ChangeStatusDialog from './ChangeStatusDialog';
import { usePlaceName } from '../../utils/placeName';
import { formatToIST } from '../../utils/dataUtils';

/*
 * ---------------------------------------------------------------------------
 * Domain colour maps (centralised — see DESIGN_GUIDE §3/§6). Values mirror the
 * theme palette tokens (success/info/secondary/warning/error + slate), so the
 * pill colours stay consistent with the rest of the app.
 * ---------------------------------------------------------------------------
 */
const STATUS_PILL = {
  'On Rent':           { dot: '#10b981', bg: '#d1fae5', text: '#059669' }, // success
  'Ready for Rent':    { dot: '#0ea5e9', bg: '#e0f2fe', text: '#0369a1' }, // info
  'Waiting for Rent':  { dot: '#6366f1', bg: '#e0e7ff', text: '#4f46e5' }, // secondary
  'On Service':        { dot: '#f59e0b', bg: '#fef3c7', text: '#b45309' }, // warning
  'Breakdown':         { dot: '#ef4444', bg: '#fee2e2', text: '#b91c1c' }, // error
  'De-hired':          { dot: '#94a3b8', bg: '#e2e8f0', text: '#475569' }, // slate
  'TCU not installed': { dot: '#fb923c', bg: '#ffedd5', text: '#c2410c' }, // orange
  'Testing':           { dot: '#0ea5e9', bg: '#e0f2fe', text: '#0369a1' }, // info
  // Commissioned but not yet inducted into operations (no lifecycle status
  // set) — e.g. the Sinoboom fleet, or a fresh install awaiting deployment.
  'Upcoming':          { dot: '#8b5cf6', bg: '#ede9fe', text: '#6d28d9' }, // violet
  'Unknown':           { dot: '#94a3b8', bg: '#e2e8f0', text: '#475569' },
};

// Last-seen freshness → coloured "signal" dot.
const SIGNAL = {
  good: { color: '#10b981', halo: 'rgba(16,185,129,.18)' },
  weak: { color: '#f59e0b', halo: 'rgba(245,158,11,.18)' },
  off:  { color: '#cbd5e1', halo: 'rgba(148,163,184,.14)' },
};

// Equipment category → avatar colour + icon.
const CATEGORY_STYLE = {
  'MEWP':         { color: '#2563eb', Icon: EngineeringIcon },
  'Tower Light':  { color: '#f59e0b', Icon: WbIncandescentIcon },
  'Battery Pack': { color: '#10b981', Icon: BatteryChargingFullIcon },
  'Test Device':  { color: '#a855f7', Icon: SettingsRemoteIcon },
};
const categoryStyle = (cat) => CATEGORY_STYLE[cat] || { color: '#2563eb', Icon: Inventory2Icon };

// --- Small helpers ---------------------------------------------------------

/** Freshness bucket from a last-seen timestamp (good ≤30m, weak ≤6h, else off). */
const signalFor = (lastSeen) => {
  if (!lastSeen) return 'off';
  const mins = (Date.now() - new Date(lastSeen).getTime()) / 60000;
  if (!Number.isFinite(mins) || mins < 0) return 'off';
  if (mins <= 30) return 'good';
  if (mins <= 360) return 'weak';
  return 'off';
};

/** Last-seen in IST as "Today · HH:mm" (same calendar day) or "24 Jun · HH:mm". */
const formatLastSeen = (ts) => {
  if (!ts) return '—';
  const day = formatToIST(ts, 'yyyy-MM-dd');
  if (day === 'N/A' || day === 'Invalid Date') return '—';
  const today = formatToIST(new Date().toISOString(), 'yyyy-MM-dd');
  const time = formatToIST(ts, 'HH:mm');
  return day === today ? `Today · ${time}` : `${formatToIST(ts, 'dd MMM')} · ${time}`;
};

/** Engine-hours meter. Prefer the last RECEIVED non-zero reading the server
 *  computed (last_engine_hours): the LATEST frame is often a partial/GPS frame
 *  that reports 0 for the hour-meter, and a raw `?? ` chain would keep that 0
 *  and mask the real value. Fall back to the latest-frame aliases, treating
 *  0/blank as "not reported" so a machine with no meter shows "—", not "0 h". */
const engineHoursOf = (d) => {
  for (const raw of [d.last_engine_hours, d.engine_working_hours, d.engine_hours, d['247']]) {
    if (raw == null || raw === '') continue;
    const n = parseFloat(raw);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return null;
};

/** Coordinates only count when finite, non-(0,0) and in range. */
const validCoords = (lat, lng) =>
  Number.isFinite(lat) && Number.isFinite(lng) &&
  !(lat === 0 && lng === 0) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180;

const csvEscape = (v) => {
  const s = v == null ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/**
 * Nearby city/village name for a GPS fix (same lookup as the detail-page map
 * chip), resolved browser-side with caching. Shows "Locating…" until known.
 * Prefers a server-provided name when the API supplies one.
 */
const PlaceNameText = ({ lat, lng, serverName }) => {
  const clientName = usePlaceName(serverName ? null : lat, serverName ? null : lng);
  const name = serverName || clientName;
  if (!name) {
    return <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>Locating…</Typography>;
  }
  return (
    <Typography className="placename" variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
      {name}
    </Typography>
  );
};

/** Status pill — coloured dot + label (colour never carries meaning alone). */
const StatusPill = ({ status, onClick }) => {
  const s = STATUS_PILL[status] || STATUS_PILL.Unknown;
  return (
    <Box
      component="span"
      onClick={onClick}
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.75,
        px: 1.25, py: 0.5, borderRadius: '8px', bgcolor: s.bg,
        cursor: onClick ? 'pointer' : 'default',
      }}
    >
      <Box component="span" sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: s.dot }} />
      <Typography component="span" sx={{ fontWeight: 700, fontSize: '11px', color: s.text, whiteSpace: 'nowrap' }}>
        {status}
      </Typography>
    </Box>
  );
};

/**
 * EquipmentPage Component
 *
 * Renders the full inventory table and filter controls.
 */
// `embedded` renders the same page inside another one (Service → Equipment
// embeds it, like DailyReportPage) — only the outer page padding is dropped.
//
// `variant` (or ?view= on the URL) picks the column set:
//   default      — the full equipment master list; Last seen (a frame arrived;
//                  the TCU is alive)
//   operations   — only the INSTALLED fleet (machines with a TCU mapped,
//                  testing excluded), with Last engine on + Last GPS on (when
//                  the machine last RAN and last had a fix).
//   service      — the installed fleet too, and no page header or KPI strip —
//                  straight to search/status/site with Export beside Site.
// The Operations Equipments header in the top bar links /devices?view=operations.
const EquipmentPage = ({ embedded = false, variant = null }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const opsView = (variant || searchParams.get('view')) === 'operations';
  const svcView = (variant || searchParams.get('view')) === 'service';
  // Department views show only machines with telematics fitted — the master
  // inventory (all ~1,500 machines) stays on the Workspace equipment page.
  const fittedOnly = opsView || svcView;

  // --- 1. Global State from Store ---
  const { state: deviceStoreState, refresh } = useDeviceStore();
  const { devices, tracking: trackingData, loading } = deviceStoreState;
  const { user } = useAuth();
  const canChangeStatus = canChangeMachineStatus(user);
  // Only the super admin may add/edit devices or back up the DB (view-only for others).
  const canEdit = isSuperAdmin(user);

  // Operations columns: when each machine last RAN (engine on) and last had a
  // GPS fix. Read from device_data by the server (cached 60s there), asked for
  // only in the Operations view and only for machines that have a tracker.
  const [activity, setActivity] = useState({});
  const trackerIds = useMemo(
    () => [...new Set((devices || []).map(d => d.device_id).filter(Boolean))].sort(),
    [devices]);
  const trackerKey = trackerIds.join(',');
  useEffect(() => {
    if (!opsView || !trackerKey) return undefined;
    let alive = true;
    const load = async () => {
      try {
        const r = await deviceManagementService.getDeviceActivity(trackerKey.split(','));
        if (alive) setActivity(r.activity || {});
      } catch (e) { /* leave the columns empty rather than break the list */ }
    };
    load();
    const id = setInterval(load, 60000);      // matches the server-side cache TTL
    return () => { alive = false; clearInterval(id); };
  }, [opsView, trackerKey]);

  // Machine status change dialog (super admin / delegated permission only)
  const [statusDialog, setStatusDialog] = useState({ open: false, device: null });

  // Add Equipment (a NEW machine in the equipments master list — no TCU needed)
  const [showAddEquipment, setShowAddEquipment] = useState(false);
  const [eqSaving, setEqSaving] = useState(false);
  const [eqForm, setEqForm] = useState({ equipment_id: '', serial_number: '', model_number: '', brand: '', category: 'MEWP' });
  const handleEqField = (e) => setEqForm(f => ({ ...f, [e.target.name]: e.target.value }));
  const handleAddEquipment = async () => {
    if (!eqForm.equipment_id.trim()) {
      setSnackbar({ open: true, message: 'Equipment ID is required.', severity: 'warning' });
      return;
    }
    setEqSaving(true);
    try {
      await deviceManagementService.addEquipment(eqForm);
      setSnackbar({ open: true, message: `Equipment ${eqForm.equipment_id.trim()} added to the fleet.`, severity: 'success' });
      setShowAddEquipment(false);
      setEqForm({ equipment_id: '', serial_number: '', model_number: '', brand: '', category: 'MEWP' });
      refresh();
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'Failed to add equipment.', severity: 'error' });
    } finally {
      setEqSaving(false);
    }
  };

  // Coming BACK from a machine's detail page restores the list exactly as it
  // was left — search, status/site filters, Upcoming toggle, page and scroll.
  // Saved on the row click (handleDeviceClick), consumed here on mount (like
  // ServicePage's return card), so entering from the top bar still starts
  // fresh. Keyed per view so Service and Operations don't overwrite each other.
  const RETURN_KEY = `equipmentReturnState:${opsView ? 'operations' : svcView ? 'service' : 'all'}`;
  const [restored] = useState(() => {
    try {
      const raw = sessionStorage.getItem(RETURN_KEY);
      sessionStorage.removeItem(RETURN_KEY); // consume
      return raw ? JSON.parse(raw) : null;
    } catch (e) { return null; }
  });

  // Text input for the search bar
  const [searchTerm, setSearchTerm] = useState(restored?.searchTerm || '');

  // --- 2. Filter State ---
  const [selectedCategories, setSelectedCategories] = useState(restored?.selectedCategories || []);
  const [selectedManufacturers] = useState([]);
  const [selectedAvailability, setSelectedAvailability] = useState(restored?.selectedAvailability || []);
  const [selectedLocation, setSelectedLocation] = useState(restored?.selectedLocation || '');
  const [isFittedFilterActive, setIsFittedFilterActive] = useState(!!restored?.isFittedFilterActive);
  // Department views split the installed fleet by lifecycle status: the
  // WORKING fleet vs UPCOMING machines (TCU-fitted but no status yet — e.g.
  // the Sinoboom fleet, fresh installs). Declared here with the other filters
  // because the page-reset effect below lists it as a dependency.
  const [showUpcoming, setShowUpcoming] = useState(!!restored?.showUpcoming);

  // --- 3. Pagination + Selection State ---
  const [page, setPage] = useState(restored?.page || 1);
  const rowsPerPage = 100;
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  // --- 4. Device Update State ---
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  // Modal mode: 'add' = register a new device, 'edit' = update an existing one
  const [dialogMode, setDialogMode] = useState('add');
  const [selectedExistingDevice, setSelectedExistingDevice] = useState(null);
  const [deviceFormField, setDeviceFormField] = useState({
    device_id: '',
    equipment_id: '',
    serial_number: '',
    device_type: 'MEWP',
    model_number: '',
    brand: '',
    product_category: 'MEWP'
  });

  const blankDeviceForm = {
    device_id: '',
    equipment_id: '',
    serial_number: '',
    device_type: 'MEWP',
    model_number: '',
    brand: '',
    product_category: 'MEWP'
  };

  // When a device is picked from the Autocomplete in edit mode, auto-fill the form
  const handleSelectExistingDevice = (device) => {
    setSelectedExistingDevice(device);
    if (!device) {
      setDeviceFormField(blankDeviceForm);
      return;
    }
    setDeviceFormField({
      device_id: device.device_id || '',
      equipment_id: device.equipment_id || '',
      serial_number: device.serial_number || '',
      device_type: device.device_type || device.product_category || 'MEWP',
      model_number: device.model_number || '',
      brand: device.brand || '',
      product_category: device.product_category || 'MEWP'
    });
  };

  // Reset everything when the modal closes
  const closeUpdateModal = () => {
    setIsUpdateModalOpen(false);
    setSelectedExistingDevice(null);
    setDeviceFormField(blankDeviceForm);
    setDialogMode('add');
  };

  // --- Data Sync ---
  // If the global store is empty, ensure fresh data on mount.
  useEffect(() => {
    if (devices.length === 0) {
      refresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Effect: Reset to page 1 when filters or search change. Compared by value
  // against the last seen filters, so mounting with restored filters keeps the
  // restored page instead of snapping back to 1.
  const filterKey = JSON.stringify([searchTerm, selectedCategories, selectedAvailability, selectedLocation, isFittedFilterActive, showUpcoming]);
  const lastFilterKey = useRef(filterKey);
  useEffect(() => {
    if (lastFilterKey.current === filterKey) return;
    lastFilterKey.current = filterKey;
    setPage(1);
  }, [filterKey]);

  // --- Data Processing (Memoization) ---

  /**
   * mergedDevices — join the static 'devices' list with live 'trackingData'
   * so each row carries both the model (static) and the lifecycle status (live).
   */
  const mergedDevices = useMemo(() => {
    return devices.map(device => {
      // Priority 1: match by Business ID (equipment_id); fallback to Device ID.
      const tracking = trackingData.find(t =>
        t.equipment_id === device.equipment_id ||
        t.equipment_id === device.device_id
      ) || null;

      const deviceInfo = parseDeviceId(device.device_id);

      // Lifecycle label (On Rent, De-hired, On Service, Breakdown, Ready/Waiting for Rent)
      let status = 'TCU not installed';
      let rawStatus = null;
      if (tracking) {
        rawStatus = (tracking.status || 'UNKNOWN').toUpperCase().replace(/\s+/g, '_');
        status = statusLabel(tracking.status);
      }
      // A TCU-fitted machine with NO lifecycle status is an "upcoming machine":
      // commissioned into the platform (e.g. the Sinoboom fleet, a fresh
      // install) but not yet inducted into operations. It joins the working
      // fleet when its status is set (adding to a contract does it automatically).
      if (device.device_id && (status === 'TCU not installed' || status === 'Unknown')) {
        status = 'Upcoming';
      }

      const mewpCategories = ['Boom Lift', 'Mast Boom - SP', 'Scissor Lift', 'Spider Lift', 'Vertical Lift', 'Vertical Mast', 'Telehandler', 'Crane'];
      const derivedProdCat = device.product_category || 'MEWP';
      // Lab/test TCUs (TEST-MTANDT, TEST-AUTOPEEPAL): not installed on any
      // machine — kept out of fleet counts and the status lifecycle.
      const isTestDevice = device.is_test
        || derivedProdCat === 'Test Device'
        || /^TEST[-_]/i.test(device.device_id || '')
        || /^TEST[-_]/i.test(device.equipment_id || '');
      const actualCategory = isTestDevice
        ? 'Test Device'
        : (mewpCategories.includes(derivedProdCat) ? 'MEWP' : (deviceInfo.type !== 'Unknown' ? deviceInfo.type : derivedProdCat));
      if (isTestDevice) {
        status = 'Testing';
        rawStatus = null;
      }

      return {
        ...device,
        category: actualCategory,
        subCategory: derivedProdCat,
        manufacturer: device.brand || 'JLG',
        status,
        rawStatus,
        site: tracking?.site_location || 'Warehouse A'
      };
    });
  }, [devices, trackingData]);

  // Upcoming machines (status 'Upcoming'/'Testing') stay out of the default
  // department list; the toolbar toggle (showUpcoming, declared with the
  // filter state above) switches the view over to them.
  const isUpcomingRow = (d) => d.status === 'Upcoming' || d.status === 'Testing';

  const upcomingCount = useMemo(
    () => (fittedOnly
      ? mergedDevices.filter(d => !!d.device_id && d.category !== 'Test Device' && isUpcomingRow(d)).length
      : 0),
    [mergedDevices, fittedOnly]);

  /**
   * baseDevices — what this view is ABOUT. The department views (Operations,
   * Service) see only the installed fleet: machines with a TCU mapped, minus
   * bench/test units — split working/upcoming by the toggle. The Workspace
   * master list sees everything.
   */
  const baseDevices = useMemo(
    () => (fittedOnly
      ? mergedDevices.filter(d => !!d.device_id && d.category !== 'Test Device' &&
          (showUpcoming ? isUpcomingRow(d) : !isUpcomingRow(d)))
      : mergedDevices),
    [mergedDevices, fittedOnly, showUpcoming]);

  /**
   * filteredDevices — apply search + the active card/dropdown filters.
   */
  const filteredDevices = useMemo(() => {
    // Test devices are a separate bucket — only shown when their card is selected.
    const showTestOnly = selectedCategories.includes('Test Device');

    return baseDevices.filter(device => {
      if (showTestOnly) {
        if (device.category !== 'Test Device') return false;
      } else if (device.category === 'Test Device') {
        return false;
      }

      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = !searchLower ||
        (device.device_id || '').toLowerCase().includes(searchLower) ||
        (device.equipment_id || '').toLowerCase().includes(searchLower) ||
        (device.serial_number || '').toLowerCase().includes(searchLower) ||
        (device.name || '').toLowerCase().includes(searchLower) ||
        (device.model_number || '').toLowerCase().includes(searchLower) ||
        (device.manufacturer || '').toLowerCase().includes(searchLower) ||
        (device.category || '').toLowerCase().includes(searchLower) ||
        (device.subCategory || '').toLowerCase().includes(searchLower);

      const matchesCategory = showTestOnly || selectedCategories.length === 0 || selectedCategories.includes(device.category);
      const matchesManufacturer = selectedManufacturers.length === 0 || selectedManufacturers.includes(device.manufacturer);
      const matchesAvailability = selectedAvailability.length === 0 || selectedAvailability.includes(device.status);
      const matchesLocation = !selectedLocation || device.site === selectedLocation;
      const matchesFitted = !isFittedFilterActive || !!device.device_id;

      return matchesSearch && matchesCategory && matchesManufacturer && matchesAvailability && matchesLocation && matchesFitted;
    });
  }, [baseDevices, searchTerm, selectedCategories, selectedManufacturers, selectedAvailability, selectedLocation, isFittedFilterActive]);

  // Distinct site locations for the Site dropdown (fleet only).
  const locationOptions = useMemo(() => {
    const set = new Set(
      baseDevices.filter(d => d.category !== 'Test Device').map(d => d.site).filter(Boolean)
    );
    return Array.from(set).sort();
  }, [baseDevices]);

  // Everything in the department views has a TCU, so that filter option is dropped there.
  const statusOptions = ['On Rent', 'Ready for Rent', 'On Service', 'Breakdown', 'De-hired', 'Waiting for Rent', 'TCU not installed']
    .filter(s => !fittedOnly || s !== 'TCU not installed');

  /** paginatedDevices — current page slice of the filtered list. */
  const paginatedDevices = useMemo(() => {
    const startIndex = (page - 1) * rowsPerPage;
    return filteredDevices.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredDevices, page, rowsPerPage]);

  // A restored page can outrun the list if the fleet shrank meanwhile.
  const pageCount = Math.ceil(filteredDevices.length / rowsPerPage);
  useEffect(() => {
    if (!loading && pageCount > 0 && page > pageCount) setPage(pageCount);
  }, [loading, page, pageCount]);

  // Put the scroll back where it was once the restored rows are on screen.
  // MainLayout's <main> is the scroll container, not the window.
  const scrollRestored = useRef(!restored?.scrollTop);
  useEffect(() => {
    if (scrollRestored.current || loading || paginatedDevices.length === 0) return;
    scrollRestored.current = true;
    requestAnimationFrame(() => {
      const main = document.querySelector('main');
      if (main) main.scrollTop = restored.scrollTop;
    });
  }, [loading, paginatedDevices.length, restored]);

  /** stats — counts for the KPI / filter cards. */
  const stats = useMemo(() => {
    // Lab/test devices are excluded from fleet KPIs (they're not installed equipment)
    const fleet = baseDevices.filter(d => d.category !== 'Test Device');
    return {
      total: fleet.length,
      available: fleet.filter(d => d.status === 'Ready for Rent').length,
      rented: fleet.filter(d => d.status === 'On Rent').length,
      maintenance: fleet.filter(d => d.status === 'On Service').length,
      breakdown: fleet.filter(d => d.status === 'Breakdown').length,
      dehired: fleet.filter(d => d.status === 'De-hired').length,
      waiting: fleet.filter(d => d.status === 'Waiting for Rent').length,
      tcu_fitted: fleet.filter(d => !!d.device_id).length,
    };
  }, [baseDevices]);

  // --- Selection helpers (drive CSV export) ---
  const rowKey = (d) => d.equipment_id || d.device_id;
  const selectedRows = useMemo(
    () => filteredDevices.filter(d => selectedIds.has(rowKey(d))),
    [filteredDevices, selectedIds]
  );
  const pageKeys = paginatedDevices.map(rowKey);
  const pageSelectedCount = pageKeys.filter(k => selectedIds.has(k)).length;
  const allPageSelected = pageKeys.length > 0 && pageSelectedCount === pageKeys.length;
  const somePageSelected = pageSelectedCount > 0 && !allPageSelected;

  const toggleRow = (key) => setSelectedIds(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const toggleAllPage = () => setSelectedIds(prev => {
    const next = new Set(prev);
    if (allPageSelected) pageKeys.forEach(k => next.delete(k));
    else pageKeys.forEach(k => next.add(k));
    return next;
  });

  // --- Event Handlers ---

  const handleDatabaseBackup = async () => {
    try {
      const response = await client.get('/database/backup', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'current_database.json');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      setSnackbar({ open: true, message: 'Database backup downloaded.', severity: 'success' });
    } catch (error) {
      console.error('Failed to process database backup:', error);
      setSnackbar({ open: true, message: 'Failed to process database backup', severity: 'error' });
    }
  };

  /** The export table (headers + row arrays) for the selected/filtered rows.
      Ops variant adds its own columns (last engine/GPS on, shift window). */
  const buildExportTable = async () => {
    const rows = selectedRows.length ? selectedRows : filteredDevices;
    if (rows.length === 0) return null;
    // EVERY machine's engine hours come from device_data: the backend walks each
    // selected machine's frames and returns the newest one that carried the
    // meter (GPS-only frames while a machine is off don't include it).
    let hoursMap = {};
    const ids = [...new Set(rows.map(d => d.device_id).filter(Boolean))];
    if (ids.length) {
      try {
        const r = await deviceManagementService.getEngineHours(ids);
        hoursMap = r.hours || {};
      } catch (e) { /* fall back to the list values below */ }
    }
    const shiftOf = (d) => {
      const a = d.device_id ? activity[d.device_id] : null;
      if (!a?.shift_start_time || !a?.shift_end_time) return '';
      return `${String(a.shift_start_time).slice(0, 5)}–${String(a.shift_end_time).slice(0, 5)}`;
    };
    // Serial number identifies the machine; no Last Seen per the export spec.
    const headers = opsView
      ? ['Serial Number', 'Model', 'Brand', 'Last Engine On', 'Last GPS On', 'Engine Hours', 'Location', 'Shift', 'Status']
      : ['Serial Number', 'Model', 'Category', 'Brand', 'Engine Hours', 'Location', 'Status'];
    const body = rows.map(d => {
      const hrs = (d.device_id && hoursMap[d.device_id] != null) ? hoursMap[d.device_id] : (engineHoursOf(d) ?? '');
      const loc = d.location_name || (d.site && d.site !== 'Warehouse A' ? d.site : '') || '';
      const act = d.device_id ? activity[d.device_id] : null;
      return opsView
        ? [d.serial_number || '', d.model_number || d.name || '', d.manufacturer || '',
           formatLastSeen(act?.last_engine_on) || '—', formatLastSeen(act?.last_gps_on) || '—',
           hrs, loc, shiftOf(d), d.status || '']
        : [d.serial_number || '', d.model_number || d.name || '', d.subCategory || d.category || '',
           d.manufacturer || '', hrs, loc, d.status || ''];
    });
    return { headers, body, count: rows.length };
  };

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    URL.revokeObjectURL(url);
  };

  /** Export the selected rows (or all filtered rows) to a CSV download. */
  const handleExport = async () => {
    const table = await buildExportTable();
    if (!table) { setSnackbar({ open: true, message: 'Nothing to export.', severity: 'info' }); return; }
    const csv = [table.headers.join(','), ...table.body.map(r => r.map(csvEscape).join(','))].join('\r\n');
    downloadBlob(new Blob([csv], { type: 'text/csv;charset=utf-8;' }), `devices_${new Date().toISOString().slice(0, 10)}.csv`);
    setSnackbar({ open: true, message: `Exported ${table.count} ${table.count === 1 ? 'device' : 'devices'} to CSV.`, severity: 'success' });
  };

  /** Excel export — an HTML table saved as .xls (opens in Excel with columns). */
  const handleExportExcel = async () => {
    const table = await buildExportTable();
    if (!table) { setSnackbar({ open: true, message: 'Nothing to export.', severity: 'info' }); return; }
    const esc = (v) => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const html = `<html xmlns:x="urn:schemas-microsoft-com:office:excel"><head><meta charset="UTF-8"></head><body><table border="1">`
      + `<tr>${table.headers.map(h => `<th>${esc(h)}</th>`).join('')}</tr>`
      + table.body.map(r => `<tr>${r.map(c => `<td>${esc(c)}</td>`).join('')}</tr>`).join('')
      + `</table></body></html>`;
    downloadBlob(new Blob([html], { type: 'application/vnd.ms-excel' }), `equipment_${new Date().toISOString().slice(0, 10)}.xls`);
    setSnackbar({ open: true, message: `Exported ${table.count} machines to Excel.`, severity: 'success' });
  };

  /** PDF export — jsPDF + autotable, loaded on demand so the page stays light. */
  const handleExportPdf = async () => {
    const table = await buildExportTable();
    if (!table) { setSnackbar({ open: true, message: 'Nothing to export.', severity: 'info' }); return; }
    try {
      const { default: JsPDF } = await import('jspdf');
      const { default: autoTable } = await import('jspdf-autotable');
      const doc = new JsPDF({ orientation: 'landscape' });
      doc.setFontSize(14);
      doc.text('Equipment', 14, 14);
      doc.setFontSize(9);
      doc.setTextColor(120);
      doc.text(`${table.count} machines · ${new Date().toLocaleDateString('en-IN')}`, 14, 20);
      autoTable(doc, {
        head: [table.headers],
        body: table.body.map(r => r.map(c => String(c ?? ''))),
        startY: 25,
        styles: { fontSize: 7.5, cellPadding: 1.5 },
        headStyles: { fillColor: [30, 58, 138] },
      });
      doc.save(`equipment_${new Date().toISOString().slice(0, 10)}.pdf`);
      setSnackbar({ open: true, message: `Exported ${table.count} machines to PDF.`, severity: 'success' });
    } catch (e) {
      console.error('PDF export failed:', e);
      setSnackbar({ open: true, message: 'PDF export failed.', severity: 'error' });
    }
  };

  // Ops/Service filter-row Export button: asks PDF or Excel, then downloads.
  const [exportAnchor, setExportAnchor] = useState(null);

  const openAddDevice = () => {
    setDialogMode('add');
    setSelectedExistingDevice(null);
    setDeviceFormField(blankDeviceForm);
    setIsUpdateModalOpen(true);
  };

  const handleSaveDevice = async () => {
    if (!deviceFormField.device_id || !deviceFormField.equipment_id) {
      setSnackbar({ open: true, message: 'Device ID and Equipment ID are required', severity: 'error' });
      return;
    }

    try {
      setIsSubmitting(true);

      const machineRecord = devices.find(d => d.equipment_id === deviceFormField.equipment_id);
      const isAlreadyTracked = machineRecord && machineRecord.device_id;

      let response;
      if (isAlreadyTracked) {
        response = await deviceManagementService.updateDeviceMetadata(deviceFormField.equipment_id, deviceFormField);
      } else {
        response = await deviceManagementService.addDeviceData(deviceFormField);
      }

      setSnackbar({ open: true, message: response.message || 'Device saved successfully', severity: 'success' });
      refresh();
      closeUpdateModal();
    } catch (error) {
      console.error('Failed to save device:', error);
      const msg = error.response?.data?.error || error.message || 'Failed to save device';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFormFieldChange = (e) => {
    const { name, value } = e.target;
    setDeviceFormField(prev => ({ ...prev, [name]: value }));
  };

  /** Toggle a status filter from a KPI card. */
  const toggleStatusFilter = (value) => {
    setSelectedAvailability(prev => (prev.includes(value) ? [] : [value]));
    setSelectedCategories([]);
    setIsFittedFilterActive(false);
  };

  const clearAllFilters = () => {
    setSearchTerm('');
    setSelectedAvailability([]);
    setSelectedLocation('');
    setSelectedCategories([]);
    setIsFittedFilterActive(false);
  };

  /** Navigate to the correct detail page based on device type. */
  const handleDeviceClick = (device) => {
    const category = (device.category || '').toLowerCase();
    const idToUse = device.equipment_id || device.device_id;
    // Remember how the list was left, so coming back restores it (see RETURN_KEY).
    try {
      sessionStorage.setItem(RETURN_KEY, JSON.stringify({
        searchTerm, selectedCategories, selectedAvailability, selectedLocation,
        isFittedFilterActive, showUpcoming, page,
        scrollTop: document.querySelector('main')?.scrollTop || 0,
      }));
    } catch (e) { /* storage unavailable — the list just opens fresh */ }
    // Every department opens the machine detail page — its "Details" button
    // holds the service record (last service, next due, the checklist), so
    // service engineers get their view without a separate page.
    // fromEquipmentList lets the detail page's Equipment breadcrumb step back here.
    const opts = { state: { fromEquipmentList: true } };
    if (category === 'mewp') {
      navigate(`/mewp-detail/${idToUse}`, opts);
    } else if (category.includes('tower')) {
      navigate(`/tower-lights/${idToUse}`, opts);
    } else {
      navigate(`/devices/${idToUse}`, opts);
    }
  };

  // --- KPI / filter cards (7-up strip) ---
  const noStatusFilter = selectedAvailability.length === 0 && selectedCategories.length === 0 && !isFittedFilterActive;
  const kpiCards = [
    { key: 'total',     label: 'Total Equipment',  value: stats.total,       icon: <Inventory2Icon />,      color: '#2563eb', active: noStatusFilter,                                  onClick: clearAllFilters },
    { key: 'fitted',    label: 'TCU Fitted',        value: stats.tcu_fitted,  icon: <SettingsRemoteIcon />,  color: '#10b981', active: isFittedFilterActive,                            onClick: () => { setIsFittedFilterActive(p => !p); setSelectedCategories([]); setSelectedAvailability([]); } },
    { key: 'dehired',   label: 'De-hired',          value: stats.dehired,     icon: <DoNotDisturbOnIcon />,  color: '#64748b', active: selectedAvailability.includes('De-hired'),       onClick: () => toggleStatusFilter('De-hired') },
    { key: 'waiting',   label: 'Waiting for Rent',  value: stats.waiting,     icon: <HourglassTopIcon />,    color: '#6366f1', active: selectedAvailability.includes('Waiting for Rent'), onClick: () => toggleStatusFilter('Waiting for Rent') },
    { key: 'available', label: 'Ready for Rent',    value: stats.available,   icon: <TaskAltIcon />,         color: '#0ea5e9', active: selectedAvailability.includes('Ready for Rent'),  onClick: () => toggleStatusFilter('Ready for Rent') },
    { key: 'service',   label: 'On Service',        value: stats.maintenance, icon: <BuildIcon />,           color: '#f59e0b', active: selectedAvailability.includes('On Service'),     onClick: () => toggleStatusFilter('On Service') },
    { key: 'breakdown', label: 'Breakdown',         value: stats.breakdown,   icon: <WarningAmberIcon />,    color: '#ef4444', active: selectedAvailability.includes('Breakdown'),      onClick: () => toggleStatusFilter('Breakdown') },
  ];

  const anyFilter = !!(searchTerm || selectedAvailability.length || selectedLocation || selectedCategories.length || isFittedFilterActive);
  const viewTotal = selectedCategories.includes('Test Device')
    ? mergedDevices.filter(d => d.category === 'Test Device').length
    : stats.total;

  // --- Main Render ---
  return (
    <Box sx={{ p: embedded ? 0 : { xs: 2, md: 3 } }} className="animate-fade-in">

      {/* Actions — no page title, the top bar already names the page. The
          Service view skips the row (its Export lives in the toolbar), and so
          does Operations when it would be empty. */}
      {!svcView && (canEdit || !opsView) && (
        <Stack direction="row" spacing={1.25} justifyContent="flex-end" sx={{ mb: 2.5 }}>
          {canEdit && (
            <Button variant="outlined" color="inherit" startIcon={<BackupIcon />} onClick={handleDatabaseBackup}
              sx={{ borderColor: 'rgba(148,163,184,.4)', color: 'text.secondary', fontWeight: 600 }}>
              Backup DB
            </Button>
          )}
          {!opsView && (
            <Button variant="outlined" color="inherit" startIcon={<ExportIcon />} onClick={handleExport}
              sx={{ borderColor: 'rgba(148,163,184,.4)', color: 'text.secondary', fontWeight: 600 }}>
              Export
            </Button>
          )}
          {canEdit && (
            <>
              <Button variant="contained" startIcon={<AddIcon />} onClick={openAddDevice} sx={{ fontWeight: 700 }}>
                Add Device
              </Button>
              <Button variant="outlined" startIcon={<EngineeringIcon />} onClick={() => setShowAddEquipment(true)}
                sx={{ fontWeight: 700 }}>
                Add Equipment
              </Button>
            </>
          )}
        </Stack>
      )}

      {/* KPI / filter cards — the Operations and Service pages skip the strip
          and go straight to search/status/site (their filters live in the toolbar). */}
      {!opsView && !svcView && (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)', lg: 'repeat(7, 1fr)' },
          gap: 1.5,
          mb: 2.25,
        }}
      >
        {kpiCards.map((kpi) => (
          <Card
            key={kpi.key}
            onClick={kpi.onClick}
            sx={{
              cursor: 'pointer',
              borderRadius: '14px',
              border: kpi.active ? `1.5px solid ${kpi.color}` : '1px solid rgba(148,163,184,0.16)',
              boxShadow: kpi.active ? `0 4px 14px ${kpi.color}30` : undefined,
              bgcolor: kpi.active ? `${kpi.color}0d` : 'background.paper',
              transition: 'border-color .18s ease, box-shadow .18s ease, background-color .18s ease',
              '&:hover': { borderColor: kpi.color },
            }}
          >
            <CardContent sx={{ p: '12px 14px !important' }}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Avatar variant="rounded" sx={{ bgcolor: `${kpi.color}1a`, color: kpi.color, width: 26, height: 26, borderRadius: '8px' }}>
                  {React.cloneElement(kpi.icon, { sx: { fontSize: 16 } })}
                </Avatar>
                <Typography sx={{ fontFamily: 'Outfit', fontWeight: 800, fontSize: '1.375rem', lineHeight: 1 }}>
                  {loading ? <Skeleton width={26} /> : Number(kpi.value).toLocaleString('en-IN')}
                </Typography>
              </Box>
              <Typography sx={{ fontWeight: 600, fontSize: '0.6875rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                {kpi.label}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>
      )}

      {/* Toolbar: search + status + site + clear + counts */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} sx={{ mb: 1.75 }} flexWrap="wrap" useFlexGap>
        <TextField
          size="small"
          placeholder="Search by serial, ID, tracker, model or brand…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ flex: 1, minWidth: 240, maxWidth: svcView ? 460 : 'none' }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: 'text.disabled' }} /></InputAdornment>,
            sx: { borderRadius: '12px', bgcolor: 'background.paper' },
          }}
        />
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={selectedAvailability[0] || ''}
            onChange={(e) => {
              const v = e.target.value;
              setSelectedAvailability(v ? [v] : []);
              setSelectedCategories([]);
              setIsFittedFilterActive(false);
            }}
            sx={{ borderRadius: '12px', bgcolor: 'background.paper' }}
          >
            <MenuItem value="">All statuses</MenuItem>
            {statusOptions.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Site</InputLabel>
          <Select
            label="Site"
            value={selectedLocation}
            onChange={(e) => setSelectedLocation(e.target.value)}
            sx={{ borderRadius: '12px', bgcolor: 'background.paper' }}
          >
            <MenuItem value="">All sites</MenuItem>
            {locationOptions.map(loc => <MenuItem key={loc} value={loc}>{loc}</MenuItem>)}
          </Select>
        </FormControl>
        {anyFilter && (
          <Button onClick={clearAllFilters} size="small" startIcon={<SearchOffIcon sx={{ fontSize: 17 }} />}
            sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
            Clear
          </Button>
        )}
        {(opsView || svcView) && (
          <>
            <Button variant="outlined" color="inherit" size="small" startIcon={<ExportIcon sx={{ fontSize: 17 }} />}
              onClick={(e) => setExportAnchor(e.currentTarget)}
              sx={{ borderColor: 'rgba(148,163,184,.4)', color: 'text.secondary', fontWeight: 700, whiteSpace: 'nowrap' }}>
              Export
            </Button>
            {/* Asks the format, then downloads. */}
            <Menu anchorEl={exportAnchor} open={!!exportAnchor} onClose={() => setExportAnchor(null)}>
              <MenuItem onClick={() => { setExportAnchor(null); handleExportPdf(); }}>Download as PDF</MenuItem>
              <MenuItem onClick={() => { setExportAnchor(null); handleExportExcel(); }}>Download as Excel</MenuItem>
            </Menu>
            {/* Upcoming machines (commissioned, no lifecycle status yet — e.g.
                Sinoboom / fresh installs) live behind this toggle, out of the
                working-fleet list the department watches. */}
            {upcomingCount > 0 && (
              <ToggleButton value="upcoming" size="small" selected={showUpcoming}
                onChange={() => setShowUpcoming(v => !v)}
                sx={{
                  textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap',
                  px: 1.25, py: 0.4, borderRadius: '10px', lineHeight: 1.6,
                  color: '#6d28d9', borderColor: 'rgba(139,92,246,.45)',
                  '&.Mui-selected': { bgcolor: '#ede9fe', color: '#6d28d9', borderColor: '#8b5cf6' },
                }}>
                Upcoming ({upcomingCount})
              </ToggleButton>
            )}
          </>
        )}
        <Typography variant="caption" sx={{ ml: { md: 'auto' }, fontWeight: 600, color: 'text.disabled' }}>
          {selectedRows.length > 0 && `${selectedRows.length} selected · `}
          {filteredDevices.length} of {viewTotal} shown
        </Typography>
      </Stack>

      {/* Devices Table */}
      <TableContainer component={Card} sx={{ borderRadius: '16px' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ pl: 2, width: 44 }} />
              <TableCell>Equipment</TableCell>
              <TableCell>Serial&nbsp;number</TableCell>
              <TableCell>Brand</TableCell>
              <TableCell>Live&nbsp;status</TableCell>
              {opsView ? (
                <>
                  <TableCell>Last&nbsp;engine&nbsp;on</TableCell>
                  <TableCell>Last&nbsp;GPS&nbsp;on</TableCell>
                </>
              ) : (
                <TableCell>Last seen</TableCell>
              )}
              <TableCell>Machine&nbsp;hours</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Status</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? [...Array(6)].map((_, i) => (
              <TableRow key={i}><TableCell colSpan={opsView ? 11 : 10}><Skeleton height={40} sx={{ my: 1 }} /></TableCell></TableRow>
            )) : paginatedDevices.map(device => {
              const key = rowKey(device);
              const cat = categoryStyle(device.category);
              const CatIcon = cat.Icon;
              const sig = SIGNAL[signalFor(device.last_seen)];
              const act = device.device_id ? activity[device.device_id] : null;
              const hrs = engineHoursOf(device);
              const lat = parseFloat(device.lat ?? device.location?.lat ?? device.location?.latitude);
              const lng = parseFloat(device.lng ?? device.location?.lon ?? device.location?.longitude);
              const hasGps = validCoords(lat, lng);
              const canEditStatus = canChangeStatus && device.rawStatus !== null;
              const liveCode = resolveDeviceStatus(device);
              const liveMeta = STATUS_META[liveCode] || STATUS_META.OFF;
              return (
                <TableRow
                  key={key}
                  hover
                  selected={selectedIds.has(key)}
                  sx={{ cursor: 'pointer' }}
                  onClick={() => handleDeviceClick(device)}
                >
                  {/* Live status dot (engine on / ignition / gps / off) */}
                  <TableCell sx={{ pl: 2, width: 44 }}>
                    <Box
                      title={liveMeta.label}
                      sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: liveMeta.hex,
                            boxShadow: `0 0 0 3px ${liveMeta.hex}22`, mx: 'auto' }}
                    />
                  </TableCell>

                  {/* Equipment: icon + model + sub */}
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <Avatar variant="rounded" sx={{ bgcolor: `${cat.color}18`, color: cat.color, width: 38, height: 38, borderRadius: '11px' }}>
                        <CatIcon sx={{ fontSize: 20 }} />
                      </Avatar>
                      <Box minWidth={0}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                          {device.model_number || device.name || 'Unnamed Equipment'}
                        </Typography>
                        <Typography variant="caption" color="text.disabled">{device.subCategory}</Typography>
                      </Box>
                    </Box>
                  </TableCell>

                  {/* Serial number (the chassis number people read off the
                      machine) leads; the equipment id / tracker sit under it. */}
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {machineLabel(device, device.device_id || '—')}
                    </Typography>
                    <Typography variant="caption" sx={{ color: isSerialMissing(device) ? 'warning.dark' : 'text.disabled', fontWeight: isSerialMissing(device) ? 600 : 400 }}>
                      {isSerialMissing(device)
                        ? 'No serial on record'
                        : [device.equipment_id, device.device_id].filter(Boolean).join(' · ')}
                    </Typography>
                  </TableCell>

                  {/* Brand */}
                  <TableCell sx={{ color: 'text.secondary', fontWeight: 500 }}>{device.manufacturer}</TableCell>

                  {/* Live status (engine on / ignition on / gps on / off) */}
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={0.75}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: liveMeta.hex, flexShrink: 0 }} />
                      <Typography variant="body2" sx={{ fontWeight: 700, color: liveMeta.hex }}>
                        {liveMeta.label}
                      </Typography>
                    </Box>
                  </TableCell>

                  {/* Operations: when the machine last RAN and last had a fix.
                      Otherwise: last seen + signal dot. */}
                  {opsView ? (
                    <>
                      <TableCell>
                        <Typography variant="body2" color={act?.last_engine_on ? 'text.secondary' : 'text.disabled'}>
                          {formatLastSeen(act?.last_engine_on)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color={act?.last_gps_on ? 'text.secondary' : 'text.disabled'}>
                          {formatLastSeen(act?.last_gps_on)}
                        </Typography>
                      </TableCell>
                    </>
                  ) : (
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={0.875}>
                        <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: sig.color, flexShrink: 0, boxShadow: `0 0 0 3px ${sig.halo}` }} />
                        <Typography variant="body2" color="text.secondary">{formatLastSeen(device.last_seen)}</Typography>
                      </Box>
                    </TableCell>
                  )}

                  {/* Machine (engine) hours — latest meter reading */}
                  <TableCell>
                    {hrs != null ? (
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {Number(hrs).toLocaleString('en-IN', { maximumFractionDigits: 1 })}
                        <Box component="span" sx={{ color: 'text.disabled', fontWeight: 500, fontSize: 11, ml: 0.375 }}>h</Box>
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.disabled">—</Typography>
                    )}
                  </TableCell>

                  {/* Location (live GPS place name, click → Google Maps; else site).
                      Operations also sees the machine's contracted shift window
                      underneath, small, so nothing else moves. */}
                  <TableCell>
                    {hasGps ? (
                      <Box
                        display="flex" alignItems="center" gap={0.5}
                        onClick={(e) => { e.stopPropagation(); window.open(`https://www.google.com/maps?q=${lat},${lng}`, '_blank'); }}
                        sx={{ cursor: 'pointer', '&:hover .placename': { textDecoration: 'underline' } }}
                      >
                        <MapPinIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                        <PlaceNameText lat={lat} lng={lng} serverName={device.location_name} />
                      </Box>
                    ) : (
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <MapPinIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                        <Typography variant="body2" color="text.disabled">
                          {device.site && device.site !== 'Warehouse A' ? device.site : 'Unknown'}
                        </Typography>
                      </Box>
                    )}
                    {opsView && act?.shift_start_time && act?.shift_end_time && (
                      <Typography variant="caption" sx={{ display: 'block', color: 'text.disabled', fontSize: 10.5, mt: 0.25, ml: '21px' }}>
                        Shift {String(act.shift_start_time).slice(0, 5)}–{String(act.shift_end_time).slice(0, 5)}
                        {act.shifts && Number(act.shifts) > 1 ? ` · ${act.shifts} shifts` : ''}
                      </Typography>
                    )}
                  </TableCell>

                  {/* Status */}
                  <TableCell>
                    <StatusPill
                      status={device.status}
                      onClick={canEditStatus ? (e) => { e.stopPropagation(); setStatusDialog({ open: true, device }); } : undefined}
                    />
                  </TableCell>

                  <TableCell align="right" sx={{ color: 'text.disabled' }}>
                    <ChevronRightIcon sx={{ fontSize: 20, verticalAlign: 'middle' }} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        {/* Empty state */}
        {!loading && filteredDevices.length === 0 && (
          <Box sx={{ p: 7, textAlign: 'center' }}>
            <SearchOffIcon sx={{ fontSize: 52, color: 'text.disabled' }} />
            <Typography variant="h6" fontWeight={700} color="text.secondary" sx={{ mt: 1 }}>
              No equipment matches your filters
            </Typography>
            <Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>
              Try clearing the search or status filter.
            </Typography>
            {anyFilter && (
              <Button onClick={clearAllFilters} sx={{ mt: 2, fontWeight: 700 }}>Clear filters</Button>
            )}
          </Box>
        )}

        {/* Footer / Pagination */}
        {!loading && filteredDevices.length > 0 && (
          <Box p={2} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.disabled' }}>
              Showing {(page - 1) * rowsPerPage + 1}–{Math.min(page * rowsPerPage, filteredDevices.length)} of {filteredDevices.length} equipment
            </Typography>
            <Pagination
              count={pageCount}
              page={page}
              onChange={(e, v) => setPage(v)}
              size="small"
              shape="rounded"
              color="primary"
            />
          </Box>
        )}
      </TableContainer>

      {/* Device Registration/Update Modal */}
      <Dialog open={isUpdateModalOpen} onClose={closeUpdateModal} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>
          {dialogMode === 'edit' ? 'Edit Existing Device' : 'Register New Device'}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" justifyContent="center" mb={2}>
            <ToggleButtonGroup
              value={dialogMode}
              exclusive
              size="small"
              onChange={(_, val) => {
                if (!val) return;
                setDialogMode(val);
                setSelectedExistingDevice(null);
                setDeviceFormField(blankDeviceForm);
              }}
              sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 700, px: 3 } }}
            >
              <ToggleButton value="add">Add New</ToggleButton>
              <ToggleButton value="edit">Edit Existing</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Typography variant="body2" color="text.secondary" mb={2}>
            {dialogMode === 'edit'
              ? 'Pick an existing device below to load its current details, then edit any field and save.'
              : 'Add a new tracker mapping to the fleet inventory.'}
          </Typography>

          {dialogMode === 'edit' && (
            <Box mb={3}>
              <Autocomplete
                options={devices}
                value={selectedExistingDevice}
                onChange={(_, v) => handleSelectExistingDevice(v)}
                getOptionLabel={(d) => d ? `${d.equipment_id || '—'}  •  ${d.device_id || 'no tracker'}` : ''}
                isOptionEqualToValue={(o, v) => o.equipment_id === v.equipment_id && o.device_id === v.device_id}
                renderOption={(props, d) => (
                  <li {...props} key={`${d.equipment_id}-${d.device_id}`}>
                    <Box>
                      <Typography variant="body2" fontWeight={700}>{d.equipment_id || '(no equipment_id)'}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Tracker: {d.device_id || '—'} • {d.model_number || 'no model'} • {d.brand || 'no brand'}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} label="Search device by Equipment ID / Tracker ID" placeholder="Start typing…" />
                )}
              />
            </Box>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth name="device_id" label="Device ID (Tracker)" value={deviceFormField.device_id} onChange={handleFormFieldChange} required variant="outlined" placeholder="e.g. TCU12345" />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth name="equipment_id" label="Asset ID (Machine)" value={deviceFormField.equipment_id} onChange={handleFormFieldChange} required variant="outlined" placeholder="e.g. LIFT-001" disabled={dialogMode === 'edit'} helperText={dialogMode === 'edit' ? 'Equipment ID is the lookup key and cannot be changed here.' : ''} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth name="serial_number" label="Serial Number" value={deviceFormField.serial_number} onChange={handleFormFieldChange} variant="outlined" />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth name="model_number" label="Model" value={deviceFormField.model_number} onChange={handleFormFieldChange} variant="outlined" />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth name="brand" label="Brand" value={deviceFormField.brand} onChange={handleFormFieldChange} variant="outlined" />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Product Category</InputLabel>
                <Select name="product_category" value={deviceFormField.product_category} label="Product Category" onChange={(e) => {
                  const val = e.target.value;
                  setDeviceFormField(prev => ({ ...prev, product_category: val, device_type: val }));
                }}>
                  <MenuItem value="MEWP">MEWP</MenuItem>
                  <MenuItem value="Tower Light">Tower Light</MenuItem>
                  <MenuItem value="Battery Pack">Battery Pack</MenuItem>
                  <MenuItem value="Telehandler">Telehandler</MenuItem>
                  <MenuItem value="Crane">Crane</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Alert severity="info">
                {dialogMode === 'edit'
                  ? 'Editing will overwrite the existing device metadata for the selected Equipment ID.'
                  : 'A new device-to-tracker mapping will be registered in the fleet inventory.'}
              </Alert>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={closeUpdateModal} sx={{ fontWeight: 700 }}>Cancel</Button>
          <Button
            onClick={handleSaveDevice}
            variant="contained"
            disabled={isSubmitting || (dialogMode === 'edit' && !selectedExistingDevice)}
            sx={{ fontWeight: 800, px: 4 }}
          >
            {isSubmitting ? 'Saving...' : (dialogMode === 'edit' ? 'Save Changes' : 'Register Device')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Machine lifecycle status change (permission-gated) */}
      <ChangeStatusDialog
        open={statusDialog.open}
        device={statusDialog.device || {}}
        currentStatus={statusDialog.device?.rawStatus}
        onClose={() => setStatusDialog({ open: false, device: null })}
        onSuccess={(newStatus) => {
          setSnackbar({ open: true, message: `Status changed to ${statusLabel(newStatus)}`, severity: 'success' });
          refresh();
        }}
      />

      {/* ===== Add Equipment (new machine in the fleet master list) ===== */}
      <Dialog open={showAddEquipment} onClose={() => !eqSaving && setShowAddEquipment(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>
          Add Equipment
          <Typography variant="body2" color="text.secondary">
            Registers a new machine in the fleet. A tracker (TCU) can be mapped to it later via Add Device.
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mt: 1 }}>
            <TextField name="equipment_id" label="Equipment ID *" value={eqForm.equipment_id} onChange={handleEqField} placeholder="e.g. A01699 / BMLDSL16005900" fullWidth />
            <TextField name="serial_number" label="Serial / Chassis No." value={eqForm.serial_number} onChange={handleEqField} placeholder="e.g. BTRC025D00110" fullWidth />
            <TextField name="model_number" label="Model" value={eqForm.model_number} onChange={handleEqField} placeholder="e.g. BT18CRT2" fullWidth />
            <TextField name="brand" label="Brand / Manufacturer" value={eqForm.brand} onChange={handleEqField} placeholder="e.g. Dingli" fullWidth />
            <TextField select name="category" label="Category" value={eqForm.category} onChange={handleEqField} fullWidth>
              {['MEWP', 'Tower Light', 'Battery Pack', 'Crane', 'Other'].map(c => (
                <MenuItem key={c} value={c}>{c}</MenuItem>
              ))}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setShowAddEquipment(false)} disabled={eqSaving}>Cancel</Button>
          <Button variant="contained" onClick={handleAddEquipment} disabled={eqSaving} sx={{ fontWeight: 700 }}>
            {eqSaving ? 'Adding…' : 'Add Equipment'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ fontWeight: 600 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EquipmentPage;
